import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

import * as Sentry from '@sentry/remix';

import { captureAdvisoryError, isAdvisoryError } from '~/services/sentry/sentryAdvisory';

import { getAppGlobals } from '../AppGlobals/utils';
import FallBack from '../Fallback';

const appGlobals = getAppGlobals();

const ErrorBoundary = () => {
  const error = useRouteError();

  // Check if this is an advisory error (external/transient, not our bug)
  // Centralized logic in sentryAdvisory.ts for consistency across the app
  const isAdvisory = isAdvisoryError(error);

  // Check if we've already tried reloading for an advisory error this session
  // Flag is set when we reload, and cleared on successful page load (see App component)
  const reloadKey = 'advisory-error-reload-attempted';
  const hasAlreadyReloaded = typeof window !== 'undefined' ? !!sessionStorage.getItem(reloadKey) : false;

  // If this is an advisory error but we've already reloaded once, treat it as a real error
  const shouldTreatAsRealError = isAdvisory && hasAlreadyReloaded;

  useEffect(() => {
    if (isRouteErrorResponse(error)) return;

    // First occurrence of advisory error: log to advisory project and reload
    if (isAdvisory && !hasAlreadyReloaded && error instanceof Error) {
      captureAdvisoryError(error, {
        tags: {
          'error-type': 'dom-manipulation',
        },
        contexts: {
          'dom-state': {
            headChildCount: typeof document !== 'undefined' ? document.head.childNodes.length : 0,
            location: typeof window !== 'undefined' ? window.location.pathname : 'unknown',
          },
        },
      });

      // Reload the page immediately - sendBeacon guarantees delivery even during page unload
      // This typically clears the issue since it's caused by external factors (browser extensions)
      sessionStorage.setItem(reloadKey, Date.now().toString());
      window.location.reload();
    } else {
      // Second occurrence of advisory error OR regular error: log to main Sentry
      Sentry.captureException(error, {
        tags: {
          keyword: 'remix-error-boundary',
          ...(shouldTreatAsRealError && { 'persisted-after-reload': 'true' }),
        },
        level: 'error',
      });
    }
  }, [error, isAdvisory, hasAlreadyReloaded, shouldTreatAsRealError]);

  // Don't render error screen for first occurrence of advisory errors (page will reload)
  // But DO show error screen if it's the second occurrence (after reload failed to fix it)
  if (isAdvisory && !hasAlreadyReloaded) {
    return null;
  }

  if (isRouteErrorResponse(error)) {
    switch (error.status) {
      case 400: {
        return <FallBack errorCode="400" publicationPresent={false} />;
      }
      case 404: {
        return (
          <FallBack
            errorCode="404"
            publicationPresent={false}
            customTitle={error.data === 'DirectLinkExpired' ? 'Uh oh, it looks like this link is expired.' : undefined}
            customDescription={
              error.data === 'DirectLinkExpired' ? (
                <>The Publication needs to update a few things on their end to resume.</>
              ) : undefined
            }
          />
        );
      }
      default: {
        return <FallBack errorCode={`${error.status}`} error={error} />;
      }
    }
  }

  // default to client error
  let errorCode = '0';
  if (error instanceof Error) {
    let errorMessage = error.message ?? '';
    // if error message is a json string, parse it and extract status and message
    // this is set in `handleError` function in entry.server.tsx
    if (errorMessage.startsWith('{')) {
      try {
        const errorJson = JSON.parse(errorMessage);
        errorCode = errorJson.status ?? '0';
        errorMessage = errorJson.message ?? '';
      } catch (_e) {
        // do nothing
      }
    }
  }

  // To make debugging easier in development.
  // biome-ignore lint/correctness/noConstantCondition: <explanation>
  if (false && process.env.NODE_ENV === 'development' && !appGlobals.ENABLE_SENTRY_DEV) {
    // biome-ignore lint/suspicious/noExplicitAny: Keeping for backward compatibility
    const unknownError: any = error;

    return (
      <div className="p-6 m-6 space-y-4 overflow-scroll text-sm text-red-600 rounded bg-red-50">
        <h1 className="text-lg font-semibold">Exception caught by root error boundary</h1>
        <p>{unknownError?.message}</p>
        <pre>{unknownError?.stack}</pre>
      </div>
    );
  }

  return <FallBack errorCode={errorCode} error={error} />;
};

export default ErrorBoundary;
